import React, { useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { Logo } from './Logo'

interface NavItem { to: string; label: string }

export function AppLayout({ navItems, orgLabel, topBarExtra }: { navItems: NavItem[]; orgLabel: string; topBarExtra?: React.ReactNode }) {
  const { me, logout } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const [resendStatus, setResendStatus] = useState<string | null>(null)

  async function resendVerification() {
    setResendStatus('Sending…')
    try {
      const result = await api('/auth/send-verification-email', { method: 'POST' })
      if (result.status === 'sent') {
        setResendStatus('Verification email sent — check your inbox.')
      } else if (result.status === 'email_not_configured') {
        // Distinct from a real failure — the request succeeded, but the
        // backend has no email provider configured yet. Surfacing this
        // separately rather than folding it into the generic error message
        // matters for whoever is debugging why no email arrived.
        setResendStatus('Email delivery is not yet configured for this environment.')
      } else {
        setResendStatus('Could not send — try again later.')
      }
    } catch {
      setResendStatus('Could not send — try again later.')
    }
  }

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <aside className="app-sidebar">
        <Logo size={28} className="sidebar-logo" />
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end className={({ isActive }) => (isActive ? 'active' : '')}>
            {item.label}
          </NavLink>
        ))}
        <div className="side-foot">
          {me?.name}
          <br />
          {me?.role}
        </div>
      </aside>
      <div className="app-main">
        <div className="app-topbar">
          <span className="org-name">{orgLabel}</span>
          {topBarExtra}
          <div className="user-chip">
            <span>{me?.email}</span>
            <Link className="btn small outline" to="/account">Account</Link>
            <button className="btn small outline" onClick={logout}>Sign out</button>
          </div>
        </div>
        {me && !me.email_verified && !dismissed && (
          <div className="card" style={{ margin: '0 var(--space-3)', marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5 }}>{resendStatus || 'Please verify your email address.'}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn small outline" onClick={resendVerification}>Resend verification email</button>
              <button className="btn small outline" onClick={() => setDismissed(true)}>Dismiss</button>
            </div>
          </div>
        )}
        <div className="app-body" id="main-content" tabIndex={-1}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
